// Line icons on a 20px grid, 1.5px stroke, currentColor. Decorative unless labelled by the caller.
import type { ReactElement } from 'react';

const S = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round' } as const;

const PATHS: Record<string, ReactElement> = {
  today: (
    <>
      <rect x="3.5" y="3" width="10" height="12.5" rx="1.5" {...S} />
      <path d="M7 17.5h8a1.5 1.5 0 0 0 1.5-1.5V6.5" {...S} />
    </>
  ),
  free: (
    <>
      <path d="M3 15.5h14" {...S} />
      <path d="M13 4.5v11" {...S} />
      <path d="M4 12.5l3-3M4 9.5l3-3M7 12.5l3-3M7 9.5l3-3" {...S} />
    </>
  ),
  lessons: (
    <>
      <path d="M3 4.5h5.5A1.5 1.5 0 0 1 10 6v10a1.5 1.5 0 0 0-1.5-1.5H3zM17 4.5h-5.5A1.5 1.5 0 0 0 10 6v10a1.5 1.5 0 0 1 1.5-1.5H17z" {...S} />
    </>
  ),
  grammar: (
    <>
      <rect x="3" y="3.5" width="14" height="13" rx="1.5" {...S} />
      <path d="M3 8h14M3 12.25h14M9 3.5v13" {...S} />
    </>
  ),
  dictionary: (
    <>
      <path d="M4 4.5A1.5 1.5 0 0 1 5.5 3H16v12H5.5A1.5 1.5 0 0 0 4 16.5zM4 16.5A1.5 1.5 0 0 0 5.5 18H16M7.5 7h5" {...S} />
    </>
  ),
  translator: (
    <>
      <path d="M3 5.5h8M7 3.5v2M9.5 5.5c-.5 3-2.5 5-5.5 6.5M5.5 8c1 1.5 2.5 2.8 4 3.5" {...S} />
      <path d="M10.5 17l3-7 3 7M11.6 14.5h3.8" {...S} />
    </>
  ),
  letters: (
    <>
      <rect x="3" y="4.5" width="14" height="11" rx="1.5" {...S} />
      <path d="M3.5 5.5 10 10.5l6.5-5" {...S} />
    </>
  ),
  review: (
    <>
      <path d="M5.5 17.5V3.5M5.5 4.25h8.75l-2 3.25 2 3.25H5.5" {...S} />
    </>
  ),
  ownWords: (
    <>
      <path d="M4 16h3l8.5-8.5a2.1 2.1 0 0 0-3-3L4 13z" {...S} />
      <path d="M11 6l3 3" {...S} />
    </>
  ),
  sentenceStack: (
    <>
      <rect x="3.5" y="11.5" width="13" height="5" rx="1.25" {...S} />
      <path d="M5 8.5h10M6.5 5.5h7" {...S} />
    </>
  ),
  settings: (
    <>
      <circle cx="10" cy="10" r="2.5" {...S} />
      <path d="M10 2.75v2M10 15.25v2M2.75 10h2M15.25 10h2M4.9 4.9l1.4 1.4M13.7 13.7l1.4 1.4M4.9 15.1l1.4-1.4M13.7 6.3l1.4-1.4" {...S} />
    </>
  ),
  more: (
    <>
      <circle cx="4.5" cy="10" r="1.25" fill="currentColor" />
      <circle cx="10" cy="10" r="1.25" fill="currentColor" />
      <circle cx="15.5" cy="10" r="1.25" fill="currentColor" />
    </>
  ),
  system: (
    <>
      <circle cx="10" cy="10" r="6.25" {...S} />
      <path d="M10 3.75a6.25 6.25 0 0 1 0 12.5z" fill="currentColor" />
    </>
  ),
  light: (
    <>
      <circle cx="10" cy="10" r="3.5" {...S} />
      <path d="M10 2v2.2M10 15.8V18M2 10h2.2M15.8 10H18M4.35 4.35l1.55 1.55M14.1 14.1l1.55 1.55M4.35 15.65l1.55-1.55M14.1 5.9l1.55-1.55" {...S} />
    </>
  ),
  dark: <path d="M16 12.4A6.6 6.6 0 0 1 7.6 4a6.6 6.6 0 1 0 8.4 8.4z" {...S} />,
  flag: <path d="M5.5 17.5V3.5M5.5 4.25h8.75l-2 3.25 2 3.25H5.5" {...S} />,
  shift: <path d="M10 3.5 3.75 10.25H7V16h6v-5.75h3.25z" {...S} />,
  shiftLock: (
    <>
      <path d="M10 3 3.75 9.25H7V13h6V9.25h3.25z" {...S} />
      <path d="M7 16.25h6" {...S} />
    </>
  ),
  backspace: (
    <>
      <path d="M7.25 4.75h8.25a1.5 1.5 0 0 1 1.5 1.5v7.5a1.5 1.5 0 0 1-1.5 1.5H7.25L2.75 10z" {...S} />
      <path d="M9.5 7.75l4.5 4.5M14 7.75l-4.5 4.5" {...S} />
    </>
  ),
  keyboard: (
    <>
      <rect x="2.5" y="5" width="15" height="10" rx="1.5" {...S} />
      <path d="M5.5 8h.01M8.5 8h.01M11.5 8h.01M14.5 8h.01M5.5 10.5h.01M14.5 10.5h.01M7.5 12.5h5" {...S} />
    </>
  ),
  arrow: <path d="M4 10h11.5M11.5 6l4 4-4 4" {...S} />,
  close: <path d="M5 5l10 10M15 5 5 15" {...S} />,
  pronunciation: (
    <>
      <path d="M3.5 8v4M6.5 5.5v9M9.5 3.5v13M12.5 6.5v7M15.5 8.5v3" {...S} />
    </>
  ),
  listen: (
    <>
      <path d="M4 13.5V11a6 6 0 0 1 12 0v2.5" {...S} />
      <rect x="3" y="12" width="3.5" height="5.5" rx="1.25" {...S} />
      <rect x="13.5" y="12" width="3.5" height="5.5" rx="1.25" {...S} />
    </>
  ),
  shared: (
    <>
      <path d="M3 15.5h14" {...S} />
      <path d="M7 11.5v4M13 8.5v7" {...S} />
      <circle cx="7" cy="8.5" r="1.75" {...S} />
      <circle cx="13" cy="5.5" r="1.75" {...S} />
    </>
  ),
  lock: (
    <>
      <rect x="4.5" y="9" width="11" height="8" rx="1.5" {...S} />
      <path d="M7 9V6.5a3 3 0 0 1 6 0V9" {...S} />
    </>
  ),
  search: (
    <>
      <circle cx="8.75" cy="8.75" r="5" {...S} />
      <path d="M12.5 12.5 16.5 16.5" {...S} />
    </>
  ),
  play: <path d="M6.5 4.5v11l9-5.5z" {...S} />,
  pause: <path d="M7 4.5v11M13 4.5v11" {...S} />,
  stop: <rect x="5.5" y="5.5" width="9" height="9" rx="1" {...S} />,
  loop: (
    <>
      <path d="M4 9V8a3 3 0 0 1 3-3h8.5M13 2.5 15.5 5 13 7.5" {...S} />
      <path d="M16 11v1a3 3 0 0 1-3 3H4.5M7 17.5 4.5 15 7 12.5" {...S} />
    </>
  ),
  mic: (
    <>
      <rect x="7.5" y="2.75" width="5" height="9" rx="2.5" {...S} />
      <path d="M4.75 9.5a5.25 5.25 0 0 0 10.5 0M10 14.75v2.5" {...S} />
    </>
  ),
  check: <path d="M4 10.5 8 14.5 16 5.5" {...S} />,
  chevron: <path d="M7.5 4.5 13 10l-5.5 5.5" {...S} />,
  back: <path d="M16 10H4.5M8.5 6l-4 4 4 4" {...S} />,
  swap: (
    <>
      <path d="M3.5 7h12M12.5 4l3 3-3 3" {...S} />
      <path d="M16.5 13h-12M7.5 10l-3 3 3 3" {...S} />
    </>
  ),
  plus: <path d="M10 4v12M4 10h12" {...S} />,
  edit: (
    <>
      <path d="M4 16h3l8.5-8.5a2.1 2.1 0 0 0-3-3L4 13z" {...S} />
    </>
  ),
  install: (
    <>
      <path d="M10 3v9.5M6.25 8.75 10 12.5l3.75-3.75" {...S} />
      <path d="M3.5 13.5v1.75a1.5 1.5 0 0 0 1.5 1.5h10a1.5 1.5 0 0 0 1.5-1.5V13.5" {...S} />
    </>
  ),
  share: (
    <>
      <path d="M10 2.75v9.5M6.75 5.75 10 2.5l3.25 3.25" {...S} />
      <path d="M7 8.5H5.5A1.5 1.5 0 0 0 4 10v6a1.5 1.5 0 0 0 1.5 1.5h9A1.5 1.5 0 0 0 16 16v-6a1.5 1.5 0 0 0-1.5-1.5H13" {...S} />
    </>
  ),
  offline: (
    <>
      <path d="M3 3l14 14" {...S} />
      <path d="M6.5 8.6A6 6 0 0 1 16 9.5M4 7a9.5 9.5 0 0 1 2-1.2M8.6 11.6a3 3 0 0 1 3.3.2" {...S} />
      <circle cx="10" cy="15" r="1" fill="currentColor" />
    </>
  ),
  // own recordings (2026-10-06): trim, delete, volume
  cut: (
    <>
      <circle cx="5.5" cy="14.5" r="2.25" {...S} />
      <circle cx="14.5" cy="14.5" r="2.25" {...S} />
      <path d="M7.2 12.9 14 3.5M12.8 12.9 6 3.5" {...S} />
    </>
  ),
  trash: (
    <>
      <path d="M3.5 5.5h13M8 5.5V3.75h4V5.5M5.25 5.5l.75 11h8l.75-11" {...S} />
    </>
  ),
  volume: (
    <>
      <path d="M3.5 8v4h3l4 3.5v-11l-4 3.5z" {...S} />
      <path d="M13.5 7.5a3.5 3.5 0 0 1 0 5M15.5 5.5a6.5 6.5 0 0 1 0 9" {...S} />
    </>
  ),
  // Pika-asetukset (layout v2): three sliders
  sliders: (
    <>
      <path d="M3 5.5h7M14 5.5h3M3 10h2M9 10h8M3 14.5h9M16 14.5h1" {...S} />
      <circle cx="12" cy="5.5" r="2" {...S} />
      <circle cx="7" cy="10" r="2" {...S} />
      <circle cx="14" cy="14.5" r="2" {...S} />
    </>
  ),
  muted: (
    <>
      <path d="M3.5 8v4h3l4 3.5v-11l-4 3.5z" {...S} />
      <path d="M13.5 8l4 4M17.5 8l-4 4" {...S} />
    </>
  ),
  // Meidän nimet: a name tag
  names: (
    <>
      <path d="M3 9.5 9.5 3H16a1 1 0 0 1 1 1v6.5L10.5 17a1.4 1.4 0 0 1-2 0L3 11.5a1.4 1.4 0 0 1 0-2z" {...S} />
      <circle cx="13" cy="7" r="1.25" {...S} />
    </>
  ),
  // Lukupino: a page of text on a pile of pages
  reading: (
    <>
      <rect x="5.5" y="2.75" width="11" height="13" rx="1.5" {...S} />
      <path d="M3.25 5.5v10.25a2 2 0 0 0 2 2h9" {...S} />
      <path d="M8.5 6.75h5M8.5 9.5h5M8.5 12.25h3" {...S} />
    </>
  ),
  // Voice Dock (2026-10-13): Peterson then you, one after the other
  seq: <path d="M3 5v10l6-5zM11 5v10l6-5z" {...S} />,
  // Voice Dock: saved, waiting for a connection
  clock: (
    <>
      <circle cx="10" cy="10" r="6.75" {...S} />
      <path d="M10 6.5v3.75l2.5 1.75" {...S} />
    </>
  ),
};

export type IconName = keyof typeof PATHS;

export function Icon({ name, size = 20, title }: { name: IconName; size?: number; title?: string }) {
  return (
    <svg
      viewBox="0 0 20 20"
      width={size}
      height={size}
      className="icon"
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      aria-label={title}
      focusable="false"
    >
      {PATHS[name]}
    </svg>
  );
}
